/**
 * Dokumente → Token-Historie (TQ #337, Auftrag 2026-09-25).
 *
 * GET /api/org/:slug/documents/token-history → { bookings: TokenBooking[] }.
 * Feldnamen gegen den echten Go-Handler geprüft (it-sce-platform, Branch
 * feat/337-cockpit-dokumente-tab, erweitert um `projekt` in
 * feat/341-cockpit-projekte-tab — erpnextbot/client.go `TokenBooking`):
 *
 *   datum: string
 *   typ: 'COGS' | 'Labor'
 *   einheiten_mio: number — IT-SCE-Token, BEREITS in Millionen (wie beim
 *     Kontingent-Balken im Dashboard), NICHT umrechnen
 *   kategorie: string
 *   taetigkeit?: string — kundentaugliches Feld Token-Buchung.taetigkeit (TQ #960, wie Regiebericht)
 *   (notizen liefert der Endpunkt nicht — interner Prozesstext, TQ #509)
 *   projekt?: string (omitempty) — Verknüpfung auf ERPNext Project, TQ #341
 *
 * Serverseitig fest auf Typ COGS+Labor gefiltert, KEIN Infra-Abzug (Kim-
 * Vorgabe 25.09.2026, siehe Handler-Kommentar org_erp_token_history.go) —
 * weicht damit vom ursprünglichen Auftragstext ("Kontingent, Verbrauch,
 * Infra-Abzüge") ab. Diese Komponente zeigt nur, was der Endpunkt liefert.
 */
import { useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useAuth } from '@it-sce/ui/contexts/KeycloakAuthContext'
import { useToast } from '../../components/ui'
import { forbiddenText } from '../../lib/forbiddenText'
import TokenBookingsTable from '../../components/TokenBookingsTable'
import { toTokenBookingRow, type TokenBookingRaw } from '../../utils/tokenBookings'

interface Organization {
  id: number
  name: string
  slug: string
  public_token?: string
  member_role: string
}

// TQ #960: Begriffe/Spalten wie in der Dashboard-Karte (utils/tokenBookings). Spalten Datum | Typ |
// Tätigkeit | Projekt | Menge; leere Felder zeigen "–".
type TokenBooking = TokenBookingRaw

interface Props {
  organization: Organization
}

export default function TokenHistory({ organization }: Props) {
  const { api: keycloakApi } = useAuth()
  const { showToast } = useToast()
  const slugOrToken = organization.public_token || organization.slug
  // TQ #341: Beidseitige Verknuepfung Token-Historie <-> Projekt. TokenHistory
  // haengt heute (nach TQ #337-Folge, Commit fd64401) ausschliesslich unter
  // .../billing, Projekte liegt als Geschwister-Route daneben. Pfad-basiert
  // statt relativer Link, damit es sowohl im Slug-Cockpit als auch auf
  // Custom-Domain (kein Slug-Praefix) funktioniert.
  const { pathname } = useLocation()
  const orgBase = pathname.replace(/\/billing\/?$/, '')

  const [bookings, setBookings] = useState<TokenBooking[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    async function load() {
      setLoading(true)
      try {
        const r = await keycloakApi!.get(`/api/org/${slugOrToken}/documents/token-history`)
        if (cancelled) return
        const list = r.data?.bookings ?? []
        setBookings(Array.isArray(list) ? list : [])
      } catch (e) {
        if (cancelled) return
        const err = e as { response?: { status?: number; data?: { error?: string } } }
        if (err.response?.status !== 404) {
          showToast('error', forbiddenText(e) ?? `Token-Historie konnte nicht geladen werden: ${err.response?.data?.error ?? 'Unbekannt'}`)
        }
        setBookings([])
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    void load()
    return () => { cancelled = true }
  }, [slugOrToken, keycloakApi, showToast])

  return (
    <div>
      <p style={{ margin: '0 0 1rem', color: '#6b7280', fontSize: '0.875rem' }}>
        Ihre Token-Buchungen — Einheit ausschließlich IT-SCE-Token, wie beim Kontingent-Balken
        im Dashboard. Zeigt KI-Arbeit und Arbeitszeit (COGS/Labor), keine Infra-Abzüge.
      </p>

      {loading ? (
        <p style={{ color: '#6b7280' }}>Lade …</p>
      ) : bookings.length === 0 ? (
        <div style={{ padding: '1.25rem 1rem', background: '#f9fafb', borderRadius: 6, color: '#6b7280', textAlign: 'center' }}>
          Noch keine Token-Buchungen für {organization.name}.
        </div>
      ) : (
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
          <TokenBookingsTable
            showProjekt
            rows={bookings.map(toTokenBookingRow)}
            renderProjekt={(p) => (
              <Link to={`${orgBase}/projekte/${encodeURIComponent(p)}`} style={{ color: '#FE4F00' }}>{p}</Link>
            )}
          />
        </div>
      )}
    </div>
  )
}
